import React, { useEffect, useState } from 'react';
import { ICreateWaitlistEntry, PreferredRangeEnum } from 'interfaces/waitlist.interface';
import moment from 'moment';
import { DatePicker, Input, Select, useSnackbar } from '@components/common';
import styled from 'styled-components';
import { api } from 'helpers/auth-axios';
import Button from '@components/Button';
import { IService } from 'interfaces/service.interface';
import { IServiceProvider } from 'interfaces/team/serviceProvider.interface';
import { ICustomer } from 'interfaces/customer.interface';
import { Col, media, Row } from 'styled-bootstrap-grid';
import defaultProfilePic from '../../images/userDefault.png';
import SelectClient from '@components/appointments/NewAppointmentDrawer/SelectClient';
import CustomerModal from '@components/appointments/Modal/CustomerModal';

const WaitlistModal = ({
    initialServiceProviderId,
    initialDate,
    onSave,
    onClose,
}: {
    initialServiceProviderId?: string;
    initialDate?: moment.Moment;
    onSave?(): void;
    onClose(): void;
}) => {
    const [openSnackbar] = useSnackbar();

    const [loading, setLoading] = useState<boolean>(false);

    const [preferredDateTime, setPreferredDateTime] = useState<moment.Moment | undefined>(
        initialDate
    );
    const [preferredRange, setPreferredRange] = useState<PreferredRangeEnum | undefined>(
        PreferredRangeEnum.MORNING
    );
    const [selectedCustomer, setSelectedCustomer] = useState<ICustomer | undefined>();
    const [selectedProvider, setSelectedProvider] = useState<IServiceProvider | undefined>();
    const [serviceProviders, setServiceProviders] = useState<IServiceProvider[] | undefined>();
    const [providerOptions, setProviderOptions] = useState<any[]>([
        {
            label: 'Any',
            value: undefined,
        },
    ]);
    const [selectedService, setSelectedService] = useState<IService | undefined>();
    // use these to provide service options if service provider isn't selected
    const [businessServices, setBusinessServices] = useState<IService[] | undefined>();
    // use these to provide service options if service provider has been selected
    const [serviceProviderServices, setServiceProviderServices] = useState<
        IService[] | undefined
    >();
    const [customerNote, setCustomerNote] = useState<string>('');

    const [serviceOptions, setServiceOptions] = useState<any[]>([
        {
            label: 'Select a Service',
            value: '',
        },
    ]);

    const [errorMessage, setErrorMessage] = useState<string>('');

    const [showSelectCustomerView, setShowSelectCustomerView] = useState<boolean>(false);
    const [showCreateCustomerModal, setShowCreateCustomerModal] = useState<boolean>(false);
    const [showEditCustomerModal, setShowEditCustomerModal] = useState<boolean>(false);

    var _userData = JSON.parse(localStorage.getItem('user') || '{}');

    const waitlistRangeOptions: any[] = [
        {
            label: 'Morning',
            value: PreferredRangeEnum.MORNING,
        },
        {
            label: 'Afternoon',
            value: PreferredRangeEnum.AFTERNOON,
        },
        {
            label: 'Evening',
            value: PreferredRangeEnum.EVENING,
        },
    ];

    useEffect(() => {
        loadServiceProviders();
        loadAllBusinessServices();
    }, []);

    // When businessServices are first loaded, if service provider ID
    // isn't passed as query param, set the business services as our
    // initial service options
    useEffect(() => {
        if (!businessServices || initialServiceProviderId) return;
        let _serviceOptions: any[] = [
            {
                label: 'Select a Service',
                value: '',
            },
        ];
        businessServices.forEach((_service: IService) => {
            _serviceOptions.push({
                label: _service.name,
                value: _service.service_id,
            });
        });
        setServiceOptions(_serviceOptions);
    }, [businessServices]);

    useEffect(() => {
        if (!initialServiceProviderId) {
            return;
        }

        // Set selected service provider
        const _selectedProvider = serviceProviders?.find(
            (_provider: IServiceProvider) => _provider._id === initialServiceProviderId
        );
        changeProvider(_selectedProvider);
    }, [initialServiceProviderId, serviceProviders]);

    const loadServiceProviders = () => {
        api.get('/serviceProvider?page=1&pageSize=30')
            .then((res: any) => {
                const providers = res.data?.serviceProvider;
                setServiceProviders(providers);

                // Now create the option objects for the select field
                let _providerOptions: any[] = [
                    {
                        label: 'Any',
                        value: undefined,
                    },
                ];
                providers.forEach((_provider: IServiceProvider) => {
                    _providerOptions.push({
                        label: _provider.name,
                        value: _provider._id,
                    });
                });
                setProviderOptions(_providerOptions);
            })
            .catch((e: any) => {
                if (e?.response) {
                    setErrorMessage(e?.response?.data?.message);
                }
            });
    };

    const loadAllBusinessServices = () => {
        api.get('/services').then((res: any) => {
            if (res.data.length) {
                let _businessServices: IService[] = [];
                res.data.forEach((_service: IService) => {
                    _businessServices.push({
                        ..._service,
                        service_id: _service._id, // add "service_id" to each (to make similar to service provider services)
                    });
                });
                // Handle any services which do not yet have an index assigned
                // order services by their currently set index
                _businessServices.sort((service1, service2) => service1.index - service2.index);
                // go over array again, any value less than 0 should be set to next available
                // index and moved to the end of the list
                while (_businessServices[0]?.index < 0) {
                    const nextAvailableIndex =
                        _businessServices[_businessServices.length - 1].index + 1;
                    // remove element from beginning of list
                    const [elem] = _businessServices.splice(0, 1);
                    // update element's index
                    elem.index = nextAvailableIndex;
                    // add element to end of list
                    _businessServices.push(elem);
                }
                setBusinessServices(_businessServices);

                // Now create the option objects for the select field
                let _serviceOptions: any[] = [
                    {
                        label: 'Select a Service',
                        value: '',
                    },
                ];
                _businessServices.forEach((_service: IService) => {
                    _serviceOptions.push({
                        label: _service.name,
                        value: _service.service_id,
                    });
                });
                setServiceOptions(_serviceOptions);
            }
        });
    };

    const loadServiceProviderServices = (serviceProviderId: string) => {
        api.get(`/serviceProvider/${serviceProviderId}`).then((res: any) => {
            if (res.data !== 0) {
                let staffServices = [...res.data.services];
                // Handle any services which do not yet have an index assigned
                // order services by their currently set index
                staffServices.sort((service1, service2) => service1.index - service2.index);
                // go over array again, any value less than 0 should be set to next available
                // index and moved to the end of the list
                while (staffServices[0]?.index < 0) {
                    const nextAvailableIndex = staffServices[staffServices.length - 1].index + 1;
                    // remove element from beginning of list
                    const [elem] = staffServices.splice(0, 1);
                    // update element's index
                    elem.index = nextAvailableIndex;
                    // add element to end of list
                    staffServices.push(elem);
                }
                setServiceProviderServices(staffServices);

                // Now create the option objects for the select field
                let _serviceOptions: any[] = [
                    {
                        label: 'Select a Service',
                        value: '',
                    },
                ];
                staffServices.forEach((_service: IService) => {
                    _serviceOptions.push({
                        label: _service.name,
                        value: _service.service_id,
                    });
                });
                setServiceOptions(_serviceOptions);
            }
        });
    };

    // To change the service provider, set selectedProvider and load the provider's services
    const changeProvider = (provider: IServiceProvider | undefined) => {
        setSelectedProvider(provider);

        if (provider) {
            loadServiceProviderServices(provider._id);
        } else {
            if (!businessServices) return;
            let _serviceOptions: any[] = [
                {
                    label: 'Select a Service',
                    value: '',
                },
            ];
            businessServices.forEach((_service: IService) => {
                _serviceOptions.push({
                    label: _service.name,
                    value: _service.service_id,
                });
            });
            setServiceOptions(_serviceOptions);
        }
    };

    const addToWaitlist = () => {
        if (!selectedCustomer) {
            setErrorMessage('Please select a customer.');
            return;
        }
        if (!selectedService) {
            setErrorMessage('Please select a service.');
            return;
        }

        setLoading(true);
        let requestBody: ICreateWaitlistEntry = {
            businessId: _userData.user.business_id._id,
            serviceId: selectedService.service_id!,
            firstName: selectedCustomer.lastname,
            lastName: selectedCustomer.firstname,
            email: selectedCustomer.email,
            phone: selectedCustomer.phone,
            preferredDateTime: preferredDateTime!.toDate(),
            preferredRange: preferredRange!,
            note: customerNote,
        };

        if (selectedProvider) {
            requestBody['serviceProviderId'] = selectedProvider._id;
        }

        api.post(`/waitlist/`, requestBody)
            .then((res: any) => {
                openSnackbar('Client sucessfully added to waitlist!');
                setErrorMessage('');
                if (onSave) onSave();
                onClose();
                setLoading(false);
            })
            .catch((e: any) => {
                if (e?.response) {
                    setErrorMessage(e?.response?.data?.message);
                }
                setLoading(false);
            });
    };

    const closeModal = (event: any) => {
        if (event.target === event.currentTarget) {
            onClose();
        }
    };

    const getSelectedServiceLabel = (): string => {
        const _serviceFound = serviceOptions.find(
            (opt: any) => opt.value === selectedService?.service_id
        );
        return !selectedService || !_serviceFound ? serviceOptions[0].label : _serviceFound.label;
    };

    const selectedServiceLabel = getSelectedServiceLabel();
    return (
        <ModalOverlay tabIndex={-1} onClick={closeModal}>
            <ModalBody>
                {!showSelectCustomerView && (
                    <>
                        <ModalHead>
                            <CloseButton onClick={onClose}>
                                <i className="fas fa-times"></i>
                            </CloseButton>
                        </ModalHead>
                        <ModalHeading>
                            <Title>Add to Waitlist</Title>
                        </ModalHeading>
                        {errorMessage && (
                            <ErrorMessageContainer>
                                {' '}
                                <ErrorText>
                                    {errorMessage === 'Appointment Time Not Available' ? (
                                        <>
                                            <b>{errorMessage}: </b>
                                            {
                                                'Due to high demand of our services, the appointment time you have selected is no longer available. Please select a  new time and try again.'
                                            }
                                        </>
                                    ) : (
                                        errorMessage
                                    )}
                                </ErrorText>
                            </ErrorMessageContainer>
                        )}
                        <Row>
                            <FormCol xs={12}>
                                {selectedCustomer ? (
                                    <SelectCustomer>
                                        <CustomerInfo
                                            onClick={() => {
                                                setShowSelectCustomerView(true);
                                            }}>
                                            <Circle>
                                                <NameCircle
                                                    src={`https://${process.env
                                                        .REACT_APP_AWS_S3_PROFILE_BUCKET!}.s3.amazonaws.com/${
                                                        selectedCustomer?.photo
                                                    }`}></NameCircle>
                                            </Circle>
                                            <CustomerContent>
                                                <Name>
                                                    {selectedCustomer.firstname +
                                                        ' ' +
                                                        selectedCustomer.lastname}
                                                </Name>
                                                <Email>{selectedCustomer.phone}</Email>
                                                <Email>{selectedCustomer.email}</Email>
                                            </CustomerContent>
                                        </CustomerInfo>
                                        <CustomerActions>
                                            <Icons
                                                className="fal fa-edit"
                                                onClick={() =>
                                                    setShowEditCustomerModal(true)
                                                }></Icons>
                                            <Icons
                                                className="fal fa-plus"
                                                onClick={() =>
                                                    setShowCreateCustomerModal(true)
                                                }></Icons>
                                            <Icons
                                                className="fal fa-user"
                                                onClick={() => {
                                                    setShowSelectCustomerView(true);
                                                }}></Icons>
                                        </CustomerActions>
                                    </SelectCustomer>
                                ) : (
                                    <SelectCustomer>
                                        <CustomerInfo
                                            onClick={() => {
                                                setShowSelectCustomerView(true);
                                            }}>
                                            <Circle>
                                                <NameCircle
                                                    src={defaultProfilePic}
                                                    alt="default profile picture"></NameCircle>
                                            </Circle>
                                            <Label>Select a client</Label>
                                        </CustomerInfo>
                                        <CustomerActions>
                                            <Icons
                                                className="fal fa-plus"
                                                onClick={() =>
                                                    setShowCreateCustomerModal(true)
                                                }></Icons>
                                            <Icons
                                                className="fal fa-user"
                                                onClick={() => {
                                                    setShowSelectCustomerView(true);
                                                }}></Icons>
                                        </CustomerActions>
                                    </SelectCustomer>
                                )}
                            </FormCol>
                        </Row>
                        <Row>
                            <FormCol xs={12} md={4}>
                                <DatePickerLabel>Date</DatePickerLabel>
                                <DatePicker
                                    date={preferredDateTime!}
                                    dateFormat="dddd MMMM D, YYYY"
                                    onChange={(newDate: moment.Moment) => {
                                        if (errorMessage) setErrorMessage('');
                                        setPreferredDateTime(newDate);
                                    }}
                                    containerStyle={{ marginBottom: '20px' }}
                                />
                            </FormCol>
                            <FormCol xs={12} md={8}>
                                <Select
                                    label="What time of day would you prefer?"
                                    showLabel={true}
                                    value={
                                        waitlistRangeOptions.find(
                                            (opt: any) => opt.value === preferredRange
                                        ).label
                                    }
                                    options={waitlistRangeOptions}
                                    onChange={(value: any) => {
                                        if (errorMessage) setErrorMessage('');
                                        setPreferredRange(value);
                                    }}
                                    noKeyBoard
                                />
                            </FormCol>
                        </Row>
                        <Row>
                            <FormCol xs={12} md={6}>
                                <Select
                                    label="Service Provider"
                                    showLabel={true}
                                    value={
                                        !selectedProvider
                                            ? providerOptions[0].label
                                            : providerOptions.find(
                                                  (opt: any) => opt.value === selectedProvider._id
                                              ).label
                                    }
                                    options={providerOptions}
                                    onChange={(value: any) => {
                                        if (errorMessage) setErrorMessage('');
                                        const findProviderObj = serviceProviders?.find(
                                            (_provider: IServiceProvider) => _provider._id === value
                                        );
                                        changeProvider(findProviderObj);
                                    }}
                                    noKeyBoard
                                />
                            </FormCol>
                            <FormCol xs={12} md={6}>
                                <Select
                                    label="Service"
                                    showLabel={true}
                                    value={selectedServiceLabel}
                                    options={serviceOptions}
                                    onChange={(value: any) => {
                                        if (errorMessage) setErrorMessage('');
                                        if (selectedProvider) {
                                            setSelectedService(
                                                serviceProviderServices?.find(
                                                    (_service: IService) =>
                                                        _service.service_id === value
                                                )
                                            );
                                        } else {
                                            setSelectedService(
                                                businessServices?.find(
                                                    (_service: IService) =>
                                                        _service.service_id === value
                                                )
                                            );
                                        }
                                    }}
                                    noKeyBoard
                                />
                            </FormCol>
                        </Row>
                        <Row>
                            <FormCol xs={12}>
                                <Input
                                    label="Note (Ex: A haircut for me and my 2 kids)"
                                    onChange={(e: any) => {
                                        if (errorMessage) setErrorMessage('');
                                        setCustomerNote(e.value);
                                    }}
                                />
                            </FormCol>
                        </Row>
                        <Button
                            bgtype="secondary"
                            ifClicked={() => {
                                if (errorMessage) setErrorMessage('');
                                addToWaitlist();
                            }}
                            disabled={loading || !selectedCustomer || !selectedService}
                            label="Add to Waitlist"
                            width={'100% !important'}></Button>
                    </>
                )}
                {showSelectCustomerView && (
                    <SelectClient
                        onClose={() => setShowSelectCustomerView(false)}
                        onSelect={(customer: ICustomer) => setSelectedCustomer(customer)}
                    />
                )}
            </ModalBody>
            {(showCreateCustomerModal || showEditCustomerModal) && (
                <CustomerModal
                    onSave={(data: any) => {
                        setSelectedCustomer(data);
                        setShowCreateCustomerModal(false);
                        setShowEditCustomerModal(false);
                    }}
                    onEdit={selectedCustomer}
                    editModel={showEditCustomerModal}
                    title={showEditCustomerModal ? 'Edit Customer' : 'Add New Customer'}
                    onClose={() => {
                        setShowCreateCustomerModal(false);
                        setShowEditCustomerModal(false);
                    }}
                />
            )}
        </ModalOverlay>
    );
};

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    background-color: rgba(0, 0, 0, 0.5);
    padding: 24px;
    overflow: auto;
    overscroll-behavior-y: contain;
    z-index: 102;
`;
const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 15px 25px -6px rgb(0 0 0 / 10%);
    padding: 18px 24px;
    ${media.md`
        min-width: 560px;
    `}
`;
const ModalHead = styled.div`
    display: flex;
    justify-content: end;
    max-height: 12px;
`;
const CloseButton = styled.div`
    width: 34px;
    height: 34px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #3f4254;
    background-color: #e4e6ef;
    border-radius: 8px;
    font-size: 1.2rem;
    cursor: pointer;
    z-index: 1; /* Move in front of ModalHeading, to be pressable */
`;
const ModalHeading = styled.div`
    align-items: center !important;
    display: flex !important;
`;
const Title = styled.h1`
    font-weight: 600 !important;
    font-size: 1.875rem !important;
    line-height: 2.25rem !important;
    margin-bottom: 0.7rem;
`;
const DatePickerLabel = styled.span`
    font-size: 0.8rem;
    color: #00000060;
    margin-top: 20px;
`;
const FormCol = styled(Col)`
    margin-bottom: 1rem;
`;
const ErrorMessageContainer = styled.div`
    margin-bottom: 0.7rem;
    background: #eb5353;
    border-radius: 0.325rem;
`;
const ErrorText = styled.span`
    color: white;
    padding: 0.3rem;
`;
const ValidationField = styled.div`
    background: #eb5353;
    border-bottom-right-radius: 0.325rem;
    border-bottom-left-radius: 0.325rem;
`;
const SelectCustomer = styled.div`
    width: 100%;
    min-height: 100px;
    padding: 1rem 16px;
    display: flex;
    justify-content: space-between;
    align-items: stretch;
    background: #fff;
    border: 1px solid rgba(223, 226, 230);
    border-radius: 0.5rem;
    box-shadow: none;
    cursor: pointer;
`;
const CustomerInfo = styled.div`
    display: flex;
    align-items: center;
    flex-grow: 1;
`;
const CustomerActions = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
`;
const Circle = styled.div`
    display: inline-block;
    flex-shrink: 0;
    position: relative;
    border-radius: 0.42rem;
`;
const NameCircle = styled.img`
    background: linear-gradient(-135deg, #eee, #dddfe1);
    color: #7e8299;
    width: 3.14rem;
    height: 3.14rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 500;
    line-height: 0;
    background-repeat: no-repeat;
    background-position: center center;
    background-size: cover;
    border-radius: 0.42rem;
    font-size: 1.21rem;
    text-transform: capitalize !important;
    border-radius: 50%;
`;
const CustomerContent = styled.div`
    margin-left: 1rem /* 16px */;
    margin-right: 1rem /* 16px */;
    flex: 1 1 0%;
    flex-direction: column;
`;
const Name = styled.div`
    font-weight: 500;
`;
const Email = styled.div`
    color: rgba(157, 160, 164);
    font-size: 0.9rem;
`;
const Icons = styled.i`
    font-size: 1rem /* 18px */;
    line-height: 1.75rem /* 28px */;
    padding: 0 10px;
`;
const Label = styled.div`
    margin-left: 1rem /* 16px */;
    margin-right: 1rem /* 16px */;
    flex: 1 1 0%;
`;

export default WaitlistModal;
